import { useMemo, useState } from 'react';

import { useSelector } from 'hooks';
import { omit } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';

import {
  AdditionalCapacity,
  AdditionalCapacityWithoutId
} from 'components/types';

import { getAdditionalCapacity } from 'RestAPIs/scenario-additional-capacity';

import { scenarioAtom } from '../../../atoms';

import DataGrid from 'components/shared/DataGrid/DataGrid';

import AdditionalCapacityStatusInformation from '../../../components/AdditionalCapacityStatusInformation';
import AddEditAdditionalCapacityDialog from '../../components/AddEditAdditionalCapacityDialog';

import { queryKeys, useDeleteAdditionalCapacity } from '../queries';
import { dateFormatter } from 'components/shared/DataGrid/utils/valueFormatters/dateFormatter';

const AdditionalCapacityGrid = () => {
  const scenario = useRecoilValue(scenarioAtom);
  const [shouldShowAddEditDialog, setShouldShowAddEditDialog] =
    useState<boolean>(false);

  const [addEditDialogData, setAddEditDialogData] = useState<
    AdditionalCapacity | AdditionalCapacityWithoutId | null
  >(null);

  const { hasCapacityPortalEditAccess } = useSelector((state) => state.grants);
  const { t } = useTranslation('common');

  const { isLoading: isFetching, data: rows } = useQuery(
    [queryKeys.ADDITIONAL_CAPACITY, scenario?.scenarioId],
    async () => {
      const { data } = await getAdditionalCapacity(
        scenario?.scenarioId as number
      );
      return data;
    },
    {
      enabled: !!scenario?.scenarioId
    }
  );

  const { mutate: deleteCapacity, isLoading: isDeleting } =
    useDeleteAdditionalCapacity();

  const isLoading = isDeleting || isFetching;

  const handleCloseAddEdit = () => {
    setShouldShowAddEditDialog(false);
    setAddEditDialogData(null);
  };

  const onClickAddRow = () => {
    setShouldShowAddEditDialog(true);
  };

  const onClickCopyRow = (id: number) => {
    setAddEditDialogData(
      omit(rows?.find(({ id: rowId }) => rowId === id), [
        'id'
      ]) as AdditionalCapacityWithoutId
    );
    setShouldShowAddEditDialog(true);
  };

  const onClickDeleteRow = async (id: number) => {
    if (scenario?.scenarioId) {
      await deleteCapacity({
        id,
        scenarioId: scenario.scenarioId
      });
    }
  };

  const onClickEditRow = (id: number) => {
    setAddEditDialogData(
      rows?.find(({ id: row }) => row === id) as AdditionalCapacity
    );
    setShouldShowAddEditDialog(true);
  };

  const onSaveCapacity = () => {
    handleCloseAddEdit();
  };

  const columns = [
    {
      field: 'sourceOrganizationalUnit',
      headerName: t('planUnitFrom'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'destinationOrganizationalUnit',
      headerName: t('planUnitTo'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'description',
      headerName: t('description'),
      flex: 1,
      minWidth: 150
    },
    {
      field: 'function',
      headerName: t('function'),
      flex: 1,
      minWidth: 150
    },
    { field: 'type', headerName: t('type') },
    { field: 'quantity', headerName: t('quantity') },
    { field: 'hours', headerName: t('hours') },
    { field: 'unit', headerName: t('unit') },
    {
      field: 'startDate',
      headerName: t('startDate'),
      valueFormatter: dateFormatter
    },
    {
      field: 'endDate',
      headerName: t('endDate'),
      valueFormatter: dateFormatter
    },
    {
      field: 'status',
      headerName: t('status'),
      renderCell: ({ row }) => <AdditionalCapacityStatusInformation row={row} />
    }
  ];

  const gridRows = useMemo(
    () =>
      rows
        ? rows.map((r) => ({
            ...r,
            sourceOrganizationalUnit: r?.sourceOrganizationalUnit?.name ?? '',
            destinationOrganizationalUnit:
              r?.destinationOrganizationalUnit?.name ?? '',
            function: r?.function?.description ?? '',
            type: t(`additionalCapacityTypes.${r.type}`),
            unit: t(`timeUnits.${r.unit}`),
            status: r.status ? t(r.status.toLowerCase()) : '',
            isEditDisabled: r.status !== t('requested'),
            isDeleteDisabled: r.status !== t('requested')
          }))
        : [],
    [rows]
  );

  return (
    <DataGrid
      columns={columns}
      rows={gridRows}
      disableActions={
        !hasCapacityPortalEditAccess || !scenario || scenario?.readOnly
      }
      onAddClick={onClickAddRow}
      onCopyClick={(id) => onClickCopyRow(id as number)}
      onEditClick={(id) => onClickEditRow(id as number)}
      onDeleteClick={(idsList) => onClickDeleteRow(idsList[0] as number)}
      loading={isLoading}
      confirmationTitle={t('capacity.deleteConfirmationDialogTitle')}
      confirmationBody={t('capacity.deleteConfirmationDialogBody')}
      addRowDialog={
        shouldShowAddEditDialog ? (
          <AddEditAdditionalCapacityDialog
            isOpen={shouldShowAddEditDialog}
            capacity={addEditDialogData}
            handleClose={handleCloseAddEdit}
            onSave={onSaveCapacity}
          />
        ) : null
      }
    />
  );
};

export default AdditionalCapacityGrid;
